/**
 * New-ticket dialog. Staff path for raising a ticket without an inbound
 * email — internal Conduct work, or a phone-call/Slack-DM client request
 * the client never bothered to email in.
 *
 * Two modes:
 *   - external: pick a client contract + an existing user on that org
 *     to attribute the request to ("raised on behalf of"). The created
 *     ticket renders identically to inbound tickets.
 *   - internal: pick an internal contract (Conduct's own ops contracts).
 *     Submitter defaults to the staff member raising it.
 *
 * On submit we POST /api/tickets and navigate straight into the new
 * ticket so the user can keep typing context, attach files, etc.
 */

import { Building2, X, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import type { Contract, TicketPriority, User } from '@beacon/core';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Textarea } from '@/components/ui/textarea.js';
import { useContracts } from '@/hooks/use-contracts.js';
import { useCreateTicket } from '@/hooks/use-tickets.js';
import { useAdminUsers } from '@/hooks/use-users.js';
import { useViewer } from '@/hooks/use-viewer.js';
import { cn } from '@/lib/cn.js';

type Mode = 'external' | 'internal';

interface Props {
  /** Boolean so the parent can simply track open/closed without owning
   *  any of the form state. */
  open: boolean;
  onClose: () => void;
}

export function NewTicketDialog({ open, onClose }: Props) {
  const navigate = useNavigate();
  const viewer = useViewer().data ?? null;
  const contractsQuery = useContracts();
  const usersQuery = useAdminUsers();
  const create = useCreateTicket();

  const [mode, setMode] = useState<Mode>('external');
  const [contractId, setContractId] = useState('');
  const [submitterUserId, setSubmitterUserId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('p3');

  // Active contracts split by mode. External mode shows client-org
  // contracts (sla + bau); internal mode shows internal-org contracts
  // (Conduct's own ops/internal). A contract carries no `organizationType`
  // directly — we infer from the user list which orgs are internal.
  const allContracts = contractsQuery.data ?? [];
  const allUsers = usersQuery.data ?? [];
  const internalOrgIds = useMemo(() => {
    return new Set(
      allUsers
        .filter((u) => u.organizationType === 'internal')
        .map((u) => u.organizationId)
    );
  }, [allUsers]);

  const visibleContracts = useMemo<Contract[]>(() => {
    return allContracts.filter((c) => {
      if (c.status !== 'active') return false;
      const isInternalOrg = internalOrgIds.has(c.organizationId);
      return mode === 'internal' ? isInternalOrg : !isInternalOrg;
    });
  }, [allContracts, internalOrgIds, mode]);

  const selectedContract = visibleContracts.find((c) => c.id === contractId);

  // For external mode, submitter must be a user on the contract's org.
  const submitterCandidates = useMemo<User[]>(() => {
    if (!selectedContract) return [];
    return allUsers.filter(
      (u) => u.organizationId === selectedContract.organizationId
    );
  }, [allUsers, selectedContract]);

  // Reset form when dialog opens. Default mode = external (most common).
  useEffect(() => {
    if (!open) return;
    setMode('external');
    setContractId('');
    setSubmitterUserId('');
    setSubject('');
    setBody('');
    setPriority('p3');
  }, [open]);

  // Default contract pick when the visible list resolves.
  useEffect(() => {
    if (!open) return;
    if (contractId && visibleContracts.some((c) => c.id === contractId))
      return;
    if (visibleContracts[0]) setContractId(visibleContracts[0].id);
  }, [open, visibleContracts, contractId]);

  // Default submitter pick. Internal mode defaults to the viewer; external
  // mode picks the first matching user on the contract's org.
  useEffect(() => {
    if (!open) return;
    if (mode === 'internal') {
      setSubmitterUserId(viewer?.userId ?? '');
      return;
    }
    if (
      submitterUserId &&
      submitterCandidates.some((u) => u.id === submitterUserId)
    )
      return;
    setSubmitterUserId(submitterCandidates[0]?.id ?? '');
  }, [open, mode, submitterCandidates, viewer, submitterUserId]);

  // Esc closes (unless mid-submit).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !create.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, create.isPending, onClose]);

  if (!open) return null;

  function handleSubmit() {
    if (!selectedContract) return toast.error('Pick a contract');
    if (!subject.trim()) return toast.error('Subject is required');
    if (!body.trim()) return toast.error('Description is required');
    if (!submitterUserId) return toast.error('Pick a submitter');

    create.mutate(
      {
        contractId: selectedContract.id,
        subject: subject.trim(),
        priority,
        source: mode === 'internal' ? 'internal' : 'portal',
        submitterUserId,
        bodyText: body.trim(),
        bodyHtml: null,
      },
      {
        onSuccess: (ticket) => {
          toast.success(`Created ${ticket.number}`);
          onClose();
          navigate(`/tickets/${ticket.number}`);
        },
      }
    );
  }

  const isLoading = contractsQuery.isLoading || usersQuery.isLoading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dialog-in-backdrop"
      onClick={create.isPending ? undefined : onClose}
    >
      <div
        className="w-full max-w-2xl mx-4 rounded-lg border border-ink-600 bg-ink-850 shadow-xl max-h-[92vh] overflow-y-auto dialog-in-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-ticket-title"
      >
        <header className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-ink-700">
          <div>
            <h2
              id="new-ticket-title"
              className="text-[15px] font-semibold text-ink-100"
            >
              New ticket
            </h2>
            <p className="text-[12.5px] text-ink-300 mt-0.5">
              Create on a client contract or as an internal Conduct HQ
              request.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={create.isPending}
            className="text-ink-300 hover:text-ink-100 focus-ring rounded-md p-1 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-4">
          <ModeTabs
            value={mode}
            onChange={setMode}
            disabled={create.isPending}
          />

          <Field label="Contract *">
            <select
              value={contractId}
              disabled={create.isPending || isLoading}
              onChange={(e) => setContractId(e.target.value)}
              className="h-9 px-2.5 text-[13px] bg-ink-800 border border-ink-600 rounded-md w-full focus-ring transition-colors"
              data-testid="ntd-contract"
            >
              {visibleContracts.length === 0 && (
                <option value="">— no active contracts —</option>
              )}
              {visibleContracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.type.toUpperCase()}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label={mode === 'external' ? 'Raised on behalf of *' : 'Submitter'}
          >
            <select
              value={submitterUserId}
              disabled={
                create.isPending || isLoading || mode === 'internal'
              }
              onChange={(e) => setSubmitterUserId(e.target.value)}
              className="h-9 px-2.5 text-[13px] bg-ink-800 border border-ink-600 rounded-md w-full focus-ring transition-colors disabled:opacity-70"
              data-testid="ntd-submitter"
            >
              {submitterCandidates.length === 0 && (
                <option value="">— pick a contract first —</option>
              )}
              {submitterCandidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} · {u.email}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Subject *">
            <Input
              value={subject}
              disabled={create.isPending}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                mode === 'external'
                  ? 'Short summary of the issue'
                  : "e.g. Swap SSD on Priya's laptop"
              }
              data-testid="ntd-subject"
            />
          </Field>

          <Field label="Description *">
            <Textarea
              rows={6}
              value={body}
              disabled={create.isPending}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What's happening? What have you already tried?"
              data-testid="ntd-body"
            />
          </Field>

          <Field label="Priority">
            <div className="flex items-center gap-1.5">
              {(['p1', 'p2', 'p3'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  disabled={create.isPending}
                  className={cn(
                    'h-8 px-3 rounded-md text-[12.5px] focus-ring border',
                    priority === p
                      ? 'bg-ink-700 border-ink-500 text-ink-100'
                      : 'bg-ink-800 border-ink-600 text-ink-300 hover:text-ink-100'
                  )}
                  data-testid={`ntd-priority-${p}`}
                >
                  {p.toUpperCase()}
                  <span className="text-ink-400 ml-1.5 text-[11.5px]">
                    {p === 'p1'
                      ? 'Urgent'
                      : p === 'p2'
                        ? 'High'
                        : 'Normal'}
                  </span>
                </button>
              ))}
            </div>
          </Field>
        </div>

        <footer className="px-5 py-3 border-t border-ink-700 flex items-center gap-2">
          <span className="text-[11.5px] text-ink-400 mono">
            {selectedContract?.type === 'sla' && priority === 'p1'
              ? 'P1 starts the SLA response clock immediately.'
              : 'SLA pauses outside business hours when applicable.'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={create.isPending}
              data-testid="ntd-submit"
            >
              {create.isPending ? 'Creating…' : 'Create ticket'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ModeTabs({
  value,
  onChange,
  disabled,
}: {
  value: Mode;
  onChange: (next: Mode) => void;
  disabled: boolean;
}) {
  const tabs: Array<{ id: Mode; label: string; icon: React.ReactNode }> = [
    {
      id: 'external',
      label: 'Client / contract',
      icon: <Building2 className="h-3.5 w-3.5" />,
    },
    {
      id: 'internal',
      label: 'Internal (Conduct HQ)',
      icon: <Users className="h-3.5 w-3.5" />,
    },
  ];
  return (
    <div className="flex items-center gap-1 bg-ink-800 border border-ink-600 rounded-md p-0.5 w-max">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          disabled={disabled}
          className={cn(
            'h-7 px-2.5 rounded-[5px] text-[12.5px] font-medium inline-flex items-center gap-1.5',
            value === t.id
              ? 'bg-ink-700 text-ink-100'
              : 'text-ink-300 hover:text-ink-100'
          )}
          data-testid={`ntd-mode-${t.id}`}
        >
          {t.icon}
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
